import { VenueDetailsBySlugResponse, useAuth, useI18N } from "common/hooks";
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { bucketUrlPrefix } from "common/config";
import { RowsPhotoAlbum } from "react-photo-album";
import "react-photo-album/rows.css";
import { MagnifyingGlassIcon, XCircleIcon, SidebarSimpleIcon } from "@phosphor-icons/react";
import { Tooltip } from "react-tooltip";
import NoSearchResults from "@/assets/images/noresults.svg";
import FullImageViewer from "@/components/FullImageViewer";
import {
  ComplexCategoriesData,
  TaggingCategoryData,
  ComplexSpacePhoto,
} from "common/types";
import CategorySidebar from "./CategorySidebar";
import FlagVenuePhoto from "@/features/flag-report/FlagVenuePhoto";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useAtom } from "jotai";
import {
  actionToPerformAfterLoginAtom,
  actionToPerformValueAfterLoginAtom,
  actionToPerformOwnerAtom,
} from "@/store/atoms";

// Skeleton placeholder component for loading images - aligned with native app approach
const ImageSkeleton = ({
  width,
  height,
}: {
  width: number;
  height: number;
}) => {
  // Colors matching native app's skeleton system
  const lightColors = {
    bg: "#E9E9EC",
    shimmer: "rgba(255, 255, 255, 0.4)",
    icon: "#BEBDC5",
  };

  const darkColors = {
    bg: "#19165C",
    shimmer: "rgba(163, 159, 232, 0.3)",
    icon: "#352FC3",
  };

  return (
    <div
      className="relative overflow-hidden rounded-lg"
      style={{
        width: "100%",
        height: "100%",
        aspectRatio: `${width}/${height}`,
      }}
    >
      {/* Light mode skeleton */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: lightColors.bg }}
      >
        <div
          className="absolute inset-0 -translate-x-full animate-shimmer"
          style={{
            background: `linear-gradient(90deg, transparent, ${lightColors.shimmer}, transparent)`,
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="w-6 h-6 rounded-full animate-pulse"
            style={{ backgroundColor: lightColors.icon }}
          />
        </div>
      </div>

      {/* Dark mode skeleton */}
      <div
        className="absolute inset-0 hidden"
        style={{ backgroundColor: darkColors.bg }}
      >
        <div
          className="absolute inset-0 -translate-x-full animate-shimmer"
          style={{
            background: `linear-gradient(90deg, transparent, ${darkColors.shimmer}, transparent)`,
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="w-6 h-6 rounded-full animate-pulse"
            style={{ backgroundColor: darkColors.icon }}
          />
        </div>
      </div>
    </div>
  );
};

type AccessibilityPhotosTabProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  // Shared state from parent
  expandedCategories: Set<number>;
  setExpandedCategories: React.Dispatch<React.SetStateAction<Set<number>>>;
  selectedCategory: number | null;
  setSelectedCategory: (categoryId: number | null) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  showMobileMenu: boolean;
  setShowMobileMenu: (show: boolean) => void;
  pageName?: string; // For analytics tracking
};

interface PhotoItem {
  path: string;
  alt_text?: string;
  category_name: string; // Lowest level category name (subCategory.name)
  category_id: number;
  main_category_name: string; // Main category name (category.name)
  main_category_id: number;
  mid_category_name?: string; // Mid level category name (option.pageTitle)
  mid_category_id?: number;
  tag_name: string;
  created_at: string;
  photo_type?: string;
  review_photos_id?: string;
  width?: number;
  height?: number;
  sort_order?: number;
  level_name?: string;
  review_level_id?: number;
  // Accessibility tags from the space
  accessibility_tags?: string[];
  accessibility_tags_count?: number;
  // Detailed accessibility tag information with sections
  accessibility_tag_details?: Array<{
    tagName: string;
    tagId: number;
    sectionName: string;
    sectionId: string;
  }>;
}

interface CategoryGroup {
  [subcategory: string]: PhotoItem[];
}

interface PhotoCategories {
  [category: string]: CategoryGroup;
}

function AccessibilityPhotosTab({
  venueDetailsResponse,
  expandedCategories,
  setExpandedCategories,
  selectedCategory,
  setSelectedCategory,
  searchTerm,
  setSearchTerm,
  showMobileMenu,
  setShowMobileMenu,
  pageName = "Photos Tab",
}: AccessibilityPhotosTabProps) {
  const i18n = useI18N();
  const [fullImageView, setFullImageView] = useState(false);
  const [currentImage, setCurrentImage] = useState<PhotoItem | null>(null);
  const [currentSubcategoryPhotos, setCurrentSubcategoryPhotos] = useState<
    PhotoItem[]
  >([]);
  const [isProcessingPhotos, setIsProcessingPhotos] = useState(true);
  const [priorityImages, setPriorityImages] = useState<Set<string>>(new Set());
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const subcategoryRefs = useRef<Record<string, Record<string, HTMLElement>>>(
    {},
  );
  const photoGridRefs = useRef<Record<string, Record<string, HTMLElement>>>({});
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  
  // Scroll spy refs
  const isProgrammaticScroll = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [activeSubcategoryId, setActiveSubcategoryId] = useState<number | null>(null);

  // Flag photo state
  const [isFlagVenuePhotoOpen, setIsFlagVenuePhotoOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<PhotoItem | null>(null);

  // Auth hooks
  const { user, isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();
  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom,
  );
  const [actionToPerformValueAfterLogin, setActionToPerformValueAfterLogin] =
    useAtom<any>(actionToPerformValueAfterLoginAtom);
  const [actionToPerformOwner, setActionToPerformOwner] = useAtom(
    actionToPerformOwnerAtom,
  );
  // Distinct owner id: the complex-venue photos tab.
  const FLAG_OWNER = "photo:complexTab";

  // Resume a photo flag begun while logged out — only this owner reacts.
  useEffect(() => {
    if (
      isLoggedIn &&
      user &&
      actionToPerformAfterLogin === "flagPhoto" &&
      actionToPerformOwner === FLAG_OWNER
    ) {
      setSelectedImage(actionToPerformValueAfterLogin);
      setIsFlagVenuePhotoOpen(true);
      setActionToPerformAfterLogin("");
      setActionToPerformValueAfterLogin("");
      setActionToPerformOwner("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, actionToPerformOwner, user, isLoggedIn]);

  // State for processed photos
  const [photosByCategory, setPhotosByCategory] = useState<PhotoCategories>({});
  const [totalPhotos, setTotalPhotos] = useState(0);

  // ARIA-live announcement state
  const [liveAnnouncement, setLiveAnnouncement] = useState("");

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  // Initialize intersection observer for priority loading
  useEffect(() => {
    intersectionObserver.current = new IntersectionObserver(
      (entries) => {
        const newPriorityImages = new Set(priorityImages);
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const imageId = entry.target.getAttribute("data-image-id");
            if (imageId) {
              newPriorityImages.add(imageId);
            }
          }
        });
        if (newPriorityImages.size !== priorityImages.size) {
          setPriorityImages(newPriorityImages);
        }
      },
      {
        rootMargin: "200px", // Start loading 200px before entering viewport
        threshold: 0.1,
      },
    );

    return () => {
      if (intersectionObserver.current) {
        intersectionObserver.current.disconnect();
      }
    };
  }, [priorityImages]);

  // Extract photos from the venue details response with non-blocking processing
  useEffect(() => {
    if (!venueDetailsResponse?.review_tags) {
      setPhotosByCategory({});
      setTotalPhotos(0);
      setIsProcessingPhotos(false);
      return;
    }

    setIsProcessingPhotos(true);

    // Use setTimeout to make processing non-blocking
    const processPhotos = () => {
      const photos: PhotoItem[] = [];
      const categories: PhotoCategories = {};

      // Function to recursively collect photos from all categories
      const collectPhotos = (
        reviewTags: (ComplexCategoriesData | TaggingCategoryData)[],
      ) => {
        reviewTags.forEach((category) => {
          if (category.categoryOptions) {
            category.categoryOptions.forEach((option) => {
              if (option.pageChildrenCategories) {
                option.pageChildrenCategories.forEach((subCategory) => {
                  if (
                    subCategory.levelOptions &&
                    subCategory.levelOptions.length > 0
                  ) {
                    subCategory.levelOptions.forEach((space) => {
                      const filterOptions = (
                        space as unknown as Record<string, unknown>
                      ).level_tags_filter_options as
                        | {
                            levelPhotos?: ComplexSpacePhoto[];
                            questionOptions?: Array<{
                              tagOptions: Array<{
                                optionTitle: string;
                                optionTitleId: number;
                              }>;
                              category_display_section_name: string;
                              category_display_section_id: number;
                            }>;
                          }
                        | undefined;

                      if (
                        filterOptions &&
                        filterOptions.levelPhotos &&
                        Array.isArray(filterOptions.levelPhotos)
                      ) {
                        filterOptions.levelPhotos.forEach(
                          (photo: ComplexSpacePhoto) => {
                            // Build accessibility tag details with proper sections from questionOptions
                            const accessibilityTagDetails: Array<{
                              tagName: string;
                              tagId: number;
                              sectionName: string;
                              sectionId: string;
                            }> = [];

                            filterOptions.questionOptions?.forEach(
                              (question) => {
                                question.tagOptions?.forEach((tag) => {
                                  accessibilityTagDetails.push({
                                    tagName: tag.optionTitle,
                                    tagId: tag.optionTitleId,
                                    sectionName:
                                      question.category_display_section_name ||
                                      i18n.get("accessibilityFeatures"),
                                    sectionId:
                                      question.category_display_section_id?.toString() ||
                                      "accessibility-features",
                                  });
                                });
                              },
                            );

                            const photoItem: PhotoItem = {
                              path: photo.path,
                              alt_text: photo.alt_text,
                              category_name: subCategory.name, // Lowest level category
                              category_id: subCategory.id,
                              main_category_name: category.name, // Main category
                              main_category_id: category.id,
                              mid_category_name: option.pageTitle, // Mid level category
                              mid_category_id: option.pageTitleId,
                              tag_name:
                                photo.tag_name ||
                                photo.category_question_name ||
                                i18n.get("general"),
                              created_at: photo.created_at,
                              photo_type: photo.photo_type,
                              review_photos_id:
                                photo.review_photos_id?.toString(),
                              width: photo.width || 400,
                              height: photo.height || 300,
                              sort_order: photo.sort_order,
                              level_name: space.level,
                              review_level_id: photo.review_level_id,
                              accessibility_tags:
                                space.level_option_titles || [],
                              accessibility_tags_count:
                                space.level_tags_count || 0,
                              accessibility_tag_details:
                                accessibilityTagDetails,
                            };

                            photos.push(photoItem);

                            // Group by main category, then by subcategory
                            if (!categories[category.name]) {
                              categories[category.name] = {};
                            }
                            if (!categories[category.name][subCategory.name]) {
                              categories[category.name][subCategory.name] = [];
                            }
                            categories[category.name][subCategory.name].push(
                              photoItem,
                            );
                          },
                        );
                      }

                      // Also check if photos are directly on the space (fallback for different data structures)
                      if (space.levelPhotos && space.levelPhotos.length > 0) {
                        space.levelPhotos.forEach(
                          (photo: ComplexSpacePhoto) => {
                            // Try to get accessibility tag details from filterOptions if available
                            const accessibilityTagDetails: Array<{
                              tagName: string;
                              tagId: number;
                              sectionName: string;
                              sectionId: string;
                            }> = [];

                            if (filterOptions?.questionOptions) {
                              filterOptions.questionOptions.forEach(
                                (question) => {
                                  question.tagOptions?.forEach((tag) => {
                                    accessibilityTagDetails.push({
                                      tagName: tag.optionTitle,
                                      tagId: tag.optionTitleId,
                                      sectionName:
                                        question.category_display_section_name ||
                                        i18n.get("accessibilityFeatures"),
                                      sectionId:
                                        question.category_display_section_id?.toString() ||
                                        "accessibility-features",
                                    });
                                  });
                                },
                              );
                            }

                            const photoItem: PhotoItem = {
                              path: photo.path,
                              alt_text: photo.alt_text,
                              category_name: subCategory.name, // Lowest level category
                              category_id: subCategory.id,
                              main_category_name: category.name, // Main category
                              main_category_id: category.id,
                              mid_category_name: option.pageTitle, // Mid level category
                              mid_category_id: option.pageTitleId,
                              tag_name:
                                photo.tag_name ||
                                photo.category_question_name ||
                                i18n.get("general"),
                              created_at: photo.created_at,
                              photo_type: photo.photo_type,
                              review_photos_id:
                                photo.review_photos_id?.toString(),
                              width: photo.width || 400,
                              height: photo.height || 300,
                              sort_order: photo.sort_order,
                              level_name: space.level,
                              review_level_id: photo.review_level_id,
                              accessibility_tags:
                                space.level_option_titles || [],
                              accessibility_tags_count:
                                space.level_tags_count || 0,
                              accessibility_tag_details:
                                accessibilityTagDetails,
                            };

                            photos.push(photoItem);

                            // Group by main category, then by subcategory
                            if (!categories[category.name]) {
                              categories[category.name] = {};
                            }
                            if (!categories[category.name][subCategory.name]) {
                              categories[category.name][subCategory.name] = [];
                            }
                            categories[category.name][subCategory.name].push(
                              photoItem,
                            );
                          },
                        );
                      }

                      // Final fallback: check for level_main_image if space has photos but no levelPhotos array
                      const spaceAny = space as any;
                      if (
                        spaceAny.level_has_photos &&
                        spaceAny.level_main_image &&
                        (!filterOptions || !filterOptions.levelPhotos) &&
                        (!space.levelPhotos || space.levelPhotos.length === 0)
                      ) {
                        // Try to get accessibility tag details from filterOptions if available
                        const accessibilityTagDetails: Array<{
                          tagName: string;
                          tagId: number;
                          sectionName: string;
                          sectionId: string;
                        }> = [];

                        if (filterOptions?.questionOptions) {
                          filterOptions.questionOptions.forEach((question) => {
                            question.tagOptions?.forEach((tag) => {
                              accessibilityTagDetails.push({
                                tagName: tag.optionTitle,
                                tagId: tag.optionTitleId,
                                sectionName:
                                  question.category_display_section_name ||
                                  "Accessibility Features",
                                sectionId:
                                  question.category_display_section_id?.toString() ||
                                  "accessibility-features",
                              });
                            });
                          });
                        }

                        const photoItem: PhotoItem = {
                          path: spaceAny.level_main_image,
                          alt_text: `Main image for ${space.level}`,
                          category_name: subCategory.name,
                          category_id: subCategory.id,
                          main_category_name: category.name,
                          main_category_id: category.id,
                          mid_category_name: option.pageTitle,
                          mid_category_id: option.pageTitleId,
                          tag_name: space.level,
                          created_at: spaceAny.level_created_at || "",
                          photo_type: "main_image",
                          review_photos_id: spaceAny.id?.toString(),
                          width: 400, // Match the fallback width used in RowsPhotoAlbum
                          height: 300, // Match the fallback height used in RowsPhotoAlbum (4:3 ratio)
                          sort_order: 1,
                          level_name: space.level,
                          review_level_id: spaceAny.id,
                          accessibility_tags: space.level_option_titles || [],
                          accessibility_tags_count: space.level_tags_count || 0,
                          accessibility_tag_details: accessibilityTagDetails,
                        };

                        photos.push(photoItem);

                        // Group by main category, then by subcategory
                        if (!categories[category.name]) {
                          categories[category.name] = {};
                        }
                        if (!categories[category.name][subCategory.name]) {
                          categories[category.name][subCategory.name] = [];
                        }
                        categories[category.name][subCategory.name].push(
                          photoItem,
                        );
                      }
                    });
                  }

                  // Recursively collect from deeper levels
                  if (subCategory.categoryOptions) {
                    collectPhotos([subCategory]);
                  }
                });
              }
            });
          }
        });
      };

      collectPhotos(venueDetailsResponse.review_tags);

      // Update state with processed data
      setPhotosByCategory(categories);
      setTotalPhotos(photos.length);
      setIsProcessingPhotos(false);

      // After processing, prioritize images that might be in the initial viewport
      setTimeout(() => {
        const viewportHeight = window.innerHeight;
        const scrollTop = window.scrollY;
        const viewportBottom = scrollTop + viewportHeight;

        // CheckIcon if any photo containers are in the initial viewport
        document.querySelectorAll("[data-image-id]").forEach((element) => {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + scrollTop;
          const elementBottom = elementTop + rect.height;

          // If element is in or near the viewport, prioritize it
          if (
            elementTop < viewportBottom + 200 &&
            elementBottom > scrollTop - 200
          ) {
            const imageId = element.getAttribute("data-image-id");
            if (imageId) {
              setPriorityImages((prev) => new Set([...prev, imageId]));
            }
          }
        });
      }, 100);
    };

    // Use setTimeout to make processing non-blocking
    const timeoutId = setTimeout(processPhotos, 0);

    return () => clearTimeout(timeoutId);
  }, [venueDetailsResponse?.review_tags]);

  // Get review tags for sidebar navigation - filter to show only categories that have photos and match search term
  const filteredReviewTags = useMemo(() => {
    if (!venueDetailsResponse?.review_tags) return [];

    // Get subcategory names that have photos
    const subcategoriesWithPhotos = new Set<string>();
    Object.entries(photosByCategory).forEach(([, subcategories]) => {
      Object.keys(subcategories).forEach((subcategoryName) => {
        subcategoriesWithPhotos.add(subcategoryName);
      });
    });

    // Filter categories to only show those with photos and matching search
    const filtered = venueDetailsResponse.review_tags
      .map((category) => {
        const filteredCategory = { ...category };

        if (filteredCategory.categoryOptions) {
          filteredCategory.categoryOptions = filteredCategory.categoryOptions
            .map((option) => {
              const filteredOption = { ...option };

              if (filteredOption.pageChildrenCategories) {
                filteredOption.pageChildrenCategories =
                  filteredOption.pageChildrenCategories.filter(
                    (subCategory) => {
                      // CheckIcon if this specific subcategory has photos
                      const hasPhotos = subcategoriesWithPhotos.has(
                        subCategory.name,
                      );

                      // If no photos, skip this subcategory
                      if (!hasPhotos && !subCategory.categoryOptions) {
                        return false;
                      }

                      // CheckIcon nested categories for photos
                      if (!hasPhotos && subCategory.categoryOptions) {
                        const hasNestedPhotos =
                          subCategory.categoryOptions.some((nestedOption) =>
                            nestedOption.pageChildrenCategories?.some(
                              (nestedSubCategory) =>
                                subcategoriesWithPhotos.has(
                                  nestedSubCategory.name,
                                ),
                            ),
                          );
                        if (!hasNestedPhotos) return false;
                      }

                      // If we have a search term, also check if this subcategory matches the search
                      if (searchTerm) {
                        const searchLower = searchTerm.toLowerCase();
                        const subcategoryMatches = subCategory.name
                          .toLowerCase()
                          .includes(searchLower);

                        // CheckIcon if any nested subcategories match
                        let nestedMatches = false;
                        if (subCategory.categoryOptions) {
                          nestedMatches = subCategory.categoryOptions.some(
                            (nestedOption) =>
                              nestedOption.pageChildrenCategories?.some(
                                (nestedSubCategory) =>
                                  nestedSubCategory.name
                                    .toLowerCase()
                                    .includes(searchLower),
                              ),
                          );
                        }

                        return subcategoryMatches || nestedMatches;
                      }

                      return true;
                    },
                  );
              }

              return filteredOption;
            })
            .filter(
              (option) =>
                // Only keep options that have visible subcategories
                option.pageChildrenCategories &&
                option.pageChildrenCategories.length > 0,
            );
        }

        return filteredCategory;
      })
      .filter(
        (category) =>
          // Only keep categories that have visible options (subcategories are already filtered by search)
          category.categoryOptions && category.categoryOptions.length > 0,
      );

    return filtered;
  }, [venueDetailsResponse?.review_tags, photosByCategory, searchTerm]);

  // Scroll spy - auto-select category in sidebar when scrolling through sections
  useEffect(() => {
    if (!scrollContainerRef.current || Object.keys(photosByCategory).length === 0) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;
    let timeoutId: NodeJS.Timeout;

    const handleScroll = () => {
      // Don't update during programmatic scrolls
      if (isProgrammaticScroll.current) {
        return;
      }

      // Debounce scroll updates to prevent jitter
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // Find which subcategory has the most visible content in the viewport
        const headerOffset = 150; // Account for sticky header and navigation
        const viewportHeight = window.innerHeight;
        let mostVisibleSubcategoryId: number | null = null;
        let maxVisibleArea = 0;

        // Iterate through all subcategories to find the most visible one
        Object.entries(subcategoryRefs.current).forEach(([, subcategories]) => {
          Object.entries(subcategories).forEach(([subcategoryName, ref]) => {
            if (ref) {
              const rect = ref.getBoundingClientRect();
              
              // Calculate how much of this section is visible in the viewport
              // Account for the sticky header by treating anything above headerOffset as "not visible"
              const visibleTop = Math.max(rect.top, headerOffset);
              const visibleBottom = Math.min(rect.bottom, viewportHeight);
              const visibleArea = Math.max(0, visibleBottom - visibleTop);
              
              // Track which section has the most visible content
              if (visibleArea > maxVisibleArea) {
                maxVisibleArea = visibleArea;
                
                // Find the subcategory ID for this name
                for (const category of filteredReviewTags) {
                  if (category.categoryOptions) {
                    for (const option of category.categoryOptions) {
                      if (option.pageChildrenCategories) {
                        for (const subCategory of option.pageChildrenCategories) {
                          if (subCategory.name === subcategoryName) {
                            mostVisibleSubcategoryId = subCategory.id;
                            break;
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          });
        });

        // Update the active subcategory if it has changed
        if (mostVisibleSubcategoryId !== null && mostVisibleSubcategoryId !== activeSubcategoryId) {
          setActiveSubcategoryId(mostVisibleSubcategoryId);
          setSelectedCategory(mostVisibleSubcategoryId);
          
          // Auto-expand parent categories to show the active subcategory
          for (const category of filteredReviewTags) {
            if (category.categoryOptions) {
              for (const option of category.categoryOptions) {
                if (option.pageChildrenCategories) {
                  const hasActiveChild = option.pageChildrenCategories.some(
                    (subCategory) => subCategory.id === mostVisibleSubcategoryId
                  );
                  if (hasActiveChild) {
                    // Expand the parent category and option
                    setExpandedCategories((prev) => {
                      const newSet = new Set(prev);
                      newSet.add(category.id);
                      newSet.add(option.pageTitleId);
                      return newSet;
                    });
                    break;
                  }
                }
              }
            }
          }
        }
      }, 100); // 100ms debounce
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [photosByCategory, filteredReviewTags, activeSubcategoryId, setSelectedCategory, setExpandedCategories]);

  // Filter photos based on search term only (no category filtering - show all photos like Accessibility tab)
  const filteredPhotos = useMemo(() => {
    let filtered = photosByCategory;

    // Filter by search term if provided
    if (searchTerm) {
      const searchFiltered: PhotoCategories = {};
      Object.entries(photosByCategory).forEach(
        ([categoryName, subcategories]) => {
          const matchingSubcategories: CategoryGroup = {};
          Object.entries(subcategories).forEach(([subcategoryName, photos]) => {
            const matchingPhotos = photos.filter(
              (photo) =>
                photo.level_name
                  ?.toLowerCase()
                  .includes(searchTerm.toLowerCase()) ||
                photo.tag_name
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()) ||
                categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                subcategoryName
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()),
            );
            if (matchingPhotos.length > 0) {
              matchingSubcategories[subcategoryName] = matchingPhotos;
            }
          });
          if (Object.keys(matchingSubcategories).length > 0) {
            searchFiltered[categoryName] = matchingSubcategories;
          }
        },
      );
      filtered = searchFiltered;
    }

    // No category filtering - always show all photos (just like Accessibility tab shows all spaces)
    return filtered;
  }, [photosByCategory, searchTerm]);

  // Announce filtered photos count via ARIA-live
  useEffect(() => {
    const photosCount = Object.values(filteredPhotos).reduce(
      (total, subcategories) =>
        total +
        Object.values(subcategories).reduce(
          (subTotal, photos) => subTotal + photos.length,
          0,
        ),
      0,
    );

    if (searchTerm) {
      // Use a timeout to debounce rapid typing
      const timeoutId = setTimeout(() => {
        setLiveAnnouncement(
          `${photosCount} ${photosCount === 1 ? "photo" : "photos"} found`,
        );
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setLiveAnnouncement("");
    }
  }, [filteredPhotos, searchTerm]);

  const handleOpenFullImage = (
    photo: PhotoItem,
    subcategoryPhotos: PhotoItem[],
  ) => {
    setCurrentImage(photo);
    setCurrentSubcategoryPhotos(subcategoryPhotos);
    setFullImageView(true);
    // Remove focus from the button to prevent blue border after closing modal
    (document.activeElement as HTMLElement)?.blur();
  };

  const handleFlagVenuePhotoClose = () => {
    setIsFlagVenuePhotoOpen(false);
  };

  const handleFlagPhoto = (image: any) => {
    if (!user || !isLoggedIn) {
      setActionToPerformAfterLogin("flagPhoto");
      setActionToPerformValueAfterLogin(image);
      setActionToPerformOwner(FLAG_OWNER);
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    setSelectedImage(image);
    setIsFlagVenuePhotoOpen(true);
  };

  // Function to prioritize images in a specific section
  const prioritizeSectionImages = useCallback(
    (categoryName: string, subcategoryName?: string) => {
      const newPriorityImages = new Set(priorityImages);

      // Find all images in the target section
      Object.entries(photosByCategory).forEach(([catName, subcategories]) => {
        if (catName === categoryName) {
          Object.entries(subcategories).forEach(([subCatName, photos]) => {
            if (!subcategoryName || subCatName === subcategoryName) {
              photos.forEach((_, index) => {
                const imageId = `${catName}-${subCatName}-${index}`;
                newPriorityImages.add(imageId);
              });
            }
          });
        }
      });

      setPriorityImages(newPriorityImages);
    },
    [photosByCategory, priorityImages],
  );

  const renderPhotoGrid = () => {
    const categoriesToShow = Object.entries(filteredPhotos);

    if (categoriesToShow.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center mt-20">
          <img
            src={NoSearchResults}
            alt={i18n.get("noPhotos")}
            className="max-w-[70%] mx-auto mb-4"
            aria-hidden="true"
          />
          <p className="text-fg-brand-strong label-m-semibold">
            {i18n.get("noPhotosAvailableForSelectedFilter")}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {categoriesToShow.map(([categoryName, subcategories]) => {
          const subcategoryCount = Object.keys(subcategories).length;

          // Initialize category refs if not already done
          if (!subcategoryRefs.current[categoryName]) {
            subcategoryRefs.current[categoryName] = {};
          }

          return (
            <section key={categoryName} data-category-section={categoryName} aria-labelledby={`category-${categoryName.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              {subcategoryCount > 0 && (
                <div className="py-3 mb-2">
                  <h2
                    className="title-m-bold text-fg-neutral-strong"
                    data-category-heading={categoryName}
                    id={`category-${categoryName.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                  >
                    {categoryName}
                  </h2>
                </div>
              )}

              <div className="space-y-8">
                {Object.entries(subcategories).map(
                  ([subcategoryName, photos]) => (
                    <div
                      key={subcategoryName}
                      className="scroll-mt-20"
                      id={`subcategory-${subcategoryName.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      ref={(el) => {
                        if (el) {
                          subcategoryRefs.current[categoryName][
                            subcategoryName
                          ] = el;
                        }
                      }}
                    >
                      <h3 className="label-m-semibold text-fg-neutral-strong mb-3">
                        {subcategoryName}{" "}
                        {photos.length > 0
                          ? `• ${photos.length} photo${photos.length !== 1 ? "s" : ""}`
                          : i18n.get("noPhotos")}
                      </h3>
                      {photos.length > 0 && (
                        <div
                          className="photo-grid-container relative"
                          data-category={categoryName}
                          data-subcategory={subcategoryName}
                        >
                          <RowsPhotoAlbum
                            photos={photos.map((image) => ({
                              src: bucketUrlPrefix + image.path,
                              width: image.width || 400,
                              height: image.height || 300,
                              alt: image.alt_text || `Photo of ${subcategoryName} in ${venueDetailsResponse?.details?.name || 'venue'}`,
                            }))}
                            spacing={8}
                            targetRowHeight={200}
                            rowConstraints={(containerWidth) => {
                              let maxPhotos = 2; // Default for mobile and iPad
                              if (containerWidth > 768) {
                                maxPhotos = 4; // Desktop screens
                              }
                              return {
                                maxPhotos,
                                minPhotos: 1,
                                singleRowMaxHeight: 350,
                              };
                            }}
                            onClick={({ index }) =>
                              handleOpenFullImage(photos[index], photos)
                            }
                            defaultContainerWidth={1200}
                            sizes={{
                              size: "1168px",
                              sizes: [
                                {
                                  viewport: "(max-width: 767px)",
                                  size: "calc(100vw - 32px)",
                                },
                                {
                                  viewport: "(max-width: 1279px)",
                                  size: "calc(100vw - 288px)",
                                },
                              ],
                            }}
                            render={{
                              image: (props, context) => {
                                const { index } = context;
                                const imageId = `${categoryName}-${subcategoryName}-${index}`;
                                const isPriority = priorityImages.has(imageId);
                                const isLoaded = loadedImages.has(imageId);
                                const currentPhoto = photos[index];

                                return (
                                  <div
                                    data-image-id={imageId}
                                    ref={(el) => {
                                      if (el && intersectionObserver.current) {
                                        intersectionObserver.current.observe(
                                          el,
                                        );
                                      }
                                    }}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      position: "relative",
                                    }}
                                  >
                                    {/* Skeleton placeholder - shown until image loads */}
                                    {!isLoaded && (
                                      <div className="absolute inset-0">
                                        <ImageSkeleton
                                          width={currentPhoto.width || 400}
                                          height={currentPhoto.height || 300}
                                        />
                                      </div>
                                    )}

                                    {/* Actual image */}
                                    <img
                                      {...props}
                                      loading={isPriority ? "eager" : "lazy"}
                                      decoding="async"
                                      fetchPriority={
                                        isPriority ? "high" : "auto"
                                      }
                                      style={{
                                        ...props.style,
                                        transition: "opacity 0.3s ease-in-out",
                                        opacity: isLoaded ? 1 : 0,
                                        position: "relative",
                                        zIndex: 1,
                                      }}
                                      onLoad={(e) => {
                                        setLoadedImages(
                                          (prev) => new Set([...prev, imageId]),
                                        );
                                        (
                                          e.target as HTMLImageElement
                                        ).style.opacity = "1";
                                      }}
                                      onError={(e) => {
                                        // Mark as loaded even on error to hide skeleton
                                        setLoadedImages(
                                          (prev) => new Set([...prev, imageId]),
                                        );
                                        // Fallback to a placeholder image if the original fails to load
                                        (e.target as HTMLImageElement).src =
                                          "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=300&fit=crop&crop=center";
                                      }}
                                    />
                                  </div>
                                );
                              },
                              button: (props, context) => {
                                const { index } = context;
                                const currentPhoto = photos[index];

                                if (!photoGridRefs.current[categoryName]) {
                                  photoGridRefs.current[categoryName] = {};
                                }

                                const altText = currentPhoto.alt_text || "";
                                const ariaLabel = altText
                                  ? `Photo ${index + 1} of ${photos.length} in ${subcategoryName}. ${altText}`
                                  : `Photo ${index + 1} of ${photos.length} in ${subcategoryName}`;

                                return (
                                  <button
                                    {...props}
                                    ref={
                                      index === 0
                                        ? (el) => {
                                            if (el) {
                                              photoGridRefs.current[
                                                categoryName
                                              ][subcategoryName] = el;
                                            }
                                          }
                                        : undefined
                                    }
                                    aria-label={ariaLabel}
                                    className={`${props.className} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg relative overflow-hidden group`}
                                    style={props.style}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleOpenFullImage(
                                          photos[index],
                                          photos,
                                        );
                                      }
                                    }}
                                  >
                                    {props.children}
                                    <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none rounded-lg z-10" />
                                  </button>
                                );
                              },
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ),
                )}
              </div>
            </section>
          );
        })}
      </div>
    );
  };

  // Show loading state while processing photos
  if (isProcessingPhotos) {
    return (
      <div className="flex flex-col md:flex-row flex-1 min-h-0">
        {/* Backdrop for mobile drawer - only visible on mobile when sidebar is open */}
        {showMobileMenu && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setShowMobileMenu(false)}
            aria-hidden="true"
          />
        )}

        {/* Left Navigation Panel - Sidebar wrapper with transitions */}
        <div
          className={`
            lg:relative lg:overflow-hidden
            ${showMobileMenu ? "lg:w-[350px] lg:flex-shrink-0" : "lg:w-0"}
            lg:transition-all lg:duration-300
          `}
        >
          <div
            className={`
              fixed top-0 left-0 right-0 bottom-0 w-full z-50
              transform transition-transform duration-300 ease-in-out
              ${showMobileMenu ? "translate-x-0" : "-translate-x-full"}
              lg:relative lg:inset-auto lg:w-full lg:transform-none lg:transition-none
            `}
            role="dialog"
            aria-modal="true"
            aria-label="Categories navigation sidebar"
          >
            <CategorySidebar
              venueDetailsResponse={venueDetailsResponse}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              expandedCategories={expandedCategories}
              setExpandedCategories={setExpandedCategories}
              showMobileMenu={showMobileMenu}
              setShowMobileMenu={setShowMobileMenu}
              filteredReviewTags={[]} // Empty while loading
              onCategorySelect={() => {}} // No-op while loading
            />
          </div>
        </div>

        {/* Loading content area */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading photos...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <h1 className="sr-only">Accessibility Photos</h1>
      
      {/* ARIA-live region for announcing photo counts */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {liveAnnouncement}
      </div>
      
      {/* Skip Navigation Link for Screen Readers */}
      <a
        href="#photos-content-area"
        onClick={(e) => {
          e.preventDefault();
          const mainContent = document.getElementById('photos-content-area');
          if (mainContent) {
            mainContent.focus();
            mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-bg-brand-primary-default focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Skip Navigation
      </a>
      
      {/* Scrollbar styles for webkit browsers (Chrome, Safari, Edge) */}
      <style>{`
 .scrollbar-hide::-webkit-scrollbar {
 width: 8px;
 height: 8px;
 }
 .scrollbar-hide::-webkit-scrollbar-track {
 background: transparent;
 }
 .scrollbar-hide::-webkit-scrollbar-thumb {
 background: transparent;
 border-radius: 4px;
 }
 .scrollbar-hide:hover::-webkit-scrollbar-thumb {
 background: #CBD5E1;
 }
 .scrollbar-hide:hover::-webkit-scrollbar-thumb:hover {
 background: #94A3B8;
 }`}</style>

      <div className="flex flex-col md:flex-row flex-1 min-h-0">
        {/* Backdrop for mobile drawer - only visible on mobile when sidebar is open */}
        {showMobileMenu && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setShowMobileMenu(false)}
            aria-hidden="true"
          />
        )}

        {/* Left Navigation Panel - Sidebar wrapper with transitions (Focus Group 1) */}
        <nav
          aria-label="Photo categories navigation"
          className={`
            lg:relative lg:overflow-hidden
            ${showMobileMenu ? "lg:w-[350px] lg:flex-shrink-0" : "lg:w-0"}
            lg:transition-all lg:duration-300
          `}
        >
          <div
            className={`
              fixed top-0 left-0 right-0 bottom-0 w-full z-50
              transform transition-transform duration-300 ease-in-out
              ${showMobileMenu ? "translate-x-0" : "-translate-x-full"}
              lg:relative lg:inset-auto lg:w-full lg:transform-none lg:transition-none
            `}
            role="dialog"
            aria-modal="true"
            aria-label="Categories navigation sidebar"
          >
            <CategorySidebar
              venueDetailsResponse={venueDetailsResponse}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              expandedCategories={expandedCategories}
              setExpandedCategories={setExpandedCategories}
              showMobileMenu={showMobileMenu}
              setShowMobileMenu={setShowMobileMenu}
              searchPlaceholder={i18n.get("spaceName")}
              filteredReviewTags={filteredReviewTags}
          onCategorySelect={(categoryId) => {
            // Find the target name and type for this category ID
            const findScrollTarget = (
              categories: (ComplexCategoriesData | TaggingCategoryData)[],
              targetId: number,
            ): { name: string; isMainCategory: boolean } | null => {
              for (const category of categories) {
                if (category.id === targetId) {
                  return { name: category.name, isMainCategory: true }; // This is a main category
                }
                if (category.categoryOptions) {
                  for (const option of category.categoryOptions) {
                    if (option.pageTitleId === targetId) {
                      return { name: category.name, isMainCategory: true }; // Return main category for option
                    }
                    if (option.pageChildrenCategories) {
                      for (const subCategory of option.pageChildrenCategories) {
                        if (subCategory.id === targetId) {
                          return {
                            name: subCategory.name,
                            isMainCategory: false,
                          }; // Return subcategory
                        }
                      }
                    }
                  }
                }
              }
              return null;
            };

            const scrollTarget = findScrollTarget(
              filteredReviewTags,
              categoryId,
            );
            if (scrollTarget) {
              // Prioritize images in the target section immediately
              if (scrollTarget.isMainCategory) {
                prioritizeSectionImages(scrollTarget.name);
              } else {
                // For subcategories, we need to find the parent category
                Object.entries(photosByCategory).forEach(
                  ([categoryName, subcategories]) => {
                    if (
                      Object.keys(subcategories).includes(scrollTarget.name)
                    ) {
                      prioritizeSectionImages(categoryName, scrollTarget.name);
                    }
                  },
                );
              }

              // Set programmatic scroll flag to prevent scroll spy from interfering
              isProgrammaticScroll.current = true;

              // Close sidebar immediately on mobile/tablet so React re-renders and
              // the animation starts before the scroll timer fires
              if (window.innerWidth < 1024) {
                setShowMobileMenu(false);
              }

              // On mobile the content section is hidden (display:none) while the sidebar
              // is open. Wait for the 300ms close animation before calling scrollIntoView;
              // on tablet/desktop the content is already visible.
              const scrollDelay = window.innerWidth < 768 ? 350 : 100;
              setTimeout(() => {
                let elementId: string;
                if (scrollTarget.isMainCategory) {
                  // Scroll to main category header
                  elementId = `category-${scrollTarget.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`;
                } else {
                  // Scroll to specific subcategory section
                  elementId = `subcategory-${scrollTarget.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`;
                }

                const element = document.getElementById(elementId);
                if (element) {
                  element.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                    inline: "nearest",
                  });
                }
              }, scrollDelay);
              
              // Reset programmatic scroll flag after scroll completes
              setTimeout(() => {
                isProgrammaticScroll.current = false;
              }, 1000);
            }

            // Don't clear selection immediately - let scroll spy handle it
            // This keeps the sidebar item highlighted after scrolling
          }}
        />
          </div>
        </nav>

        {/* Right Content Area (Focus Group 2) */}
        <section
          id="photos-content-area"
          aria-label="Accessibility photos content"
          tabIndex={-1}
          className={`flex-1 flex flex-col min-h-0 focus:outline-none ${showMobileMenu ? "hidden md:flex" : "flex"}`}
        >
          {/* Desktop Toggle Button and Header */}
          <div className="hidden md:flex items-center gap-3 px-6 pt-4 ">
            <button
              id="category-sidebar-toggle-photos-btn"
              type="button"
              onClick={() => {
                const newState = !showMobileMenu;
                setShowMobileMenu(newState);
              }}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-bg-brand-secondary-hover transition-colors"
              aria-label={i18n.get("toggleSidebar")}
              aria-expanded={showMobileMenu}
            >
              <SidebarSimpleIcon size={20} className="text-fg-neutral-strong" />
            </button>
            <Tooltip
              anchorSelect="#category-sidebar-toggle-photos-btn"
              content={showMobileMenu ? i18n.get("hideCategories") : i18n.get("showCategories")}
              place="bottom"
              style={{ zIndex: 9999 }}
            />
            <div className="w-px h-6 bg-border-token-neutral-minimal" aria-hidden="true" />
            <h2 className="title-s-semibold text-fg-neutral-strong">
              {totalPhotos} {totalPhotos === 1 ? i18n.get("photo") : i18n.get("photos")}
            </h2>
          </div>

          {/* Mobile Navigation Header */}
          <div className="md:hidden p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => {
                  setShowMobileMenu(true);
                }}
                className="flex items-center gap-2 text-fg-brand-subtle font-medium"
                aria-label="Open categories menu"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
                Categories
              </button>
            </div>

            {/* Mobile Search Input */}
            <div className="relative">
              <label htmlFor="photos-mobile-search" className="sr-only">
                Search photos
              </label>
              <input
                id="photos-mobile-search"
                type="text"
                placeholder={i18n.get("spaceName")}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-4 pr-10 py-2 text-base md:text-[13px] border border-gray-400 rounded-lg bg-bg-brand-secondary-default text-gray-900 placeholder-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm ? (
                <button
                  onClick={() => {
                    setSearchTerm("");
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800 transition-colors p-1"
                  aria-label="Clear search"
                >
                  <XCircleIcon size={20} aria-hidden="true" />
                </button>
              ) : (
                <MagnifyingGlassIcon
                  size={20}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
                  aria-hidden="true"
                />
              )}
            </div>
          </div>

          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto scrollbar-hide hover:scrollbar-show"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "transparent transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.scrollbarColor = "#CBD5E1 transparent";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.scrollbarColor = "transparent transparent";
            }}
          >
            <div className="p-4 md:p-6">
              {/* Photo Grid */}
              {Object.keys(filteredPhotos).length === 0 ? (
                <div className="flex flex-col items-center justify-center mt-20">
                  <img
                    src={NoSearchResults}
                    alt={i18n.get("noPhotos")}
                    className="max-w-[70%] mx-auto mb-4"
                    aria-hidden="true"
                  />
                  <p className="text-fg-brand-strong label-m-semibold">
                    {totalPhotos === 0
                      ? i18n.get("noPhotosAvailable")
                      : i18n.get("noPhotosMatchSearch")}
                  </p>
                  {totalPhotos === 0 && (
                    <p className="label-s-medium text-fg-brand-strong mt-2">
                      {i18n.get("noAccessibilitySpacesAvailable")}
                    </p>
                  )}
                </div>
              ) : (
                renderPhotoGrid()
              )}
            </div>
          </div>
        </section>

        {/* Full image viewer */}
        {fullImageView && currentImage && (
          <FullImageViewer
            images={currentSubcategoryPhotos.map((photo) => ({
              src: bucketUrlPrefix + photo.path,
              alt: photo.alt_text || "",
              tag_name: photo.level_name || i18n.get("unknownSpace"),
              category_name:
                photo.mid_category_name &&
                photo.mid_category_name !== photo.category_name
                  ? `${photo.main_category_name} / ${photo.mid_category_name} / ${photo.category_name}`
                  : `${photo.main_category_name} / ${photo.category_name}`,
              created_at: photo.created_at,
              photo_type: photo.photo_type,
              review_photos_id: photo.review_photos_id,
              width: photo.width,
              height: photo.height,
              sort_order: photo.sort_order,
              qtags: photo.accessibility_tag_details?.length
                ? // Use detailed tag information with proper sections
                  photo.accessibility_tag_details.map((tagDetail) => {
                    const qtagItem = {
                      tagId: tagDetail.tagId,
                      tagName: tagDetail.tagName,
                      categoryDisplaySectionId: tagDetail.sectionId, // Use section ID to match displaySections keys
                    };

                    return qtagItem;
                  })
                : // Fallback to basic accessibility tags
                  photo.accessibility_tags?.map((tagName, index) => {
                    const filterTag =
                      venueDetailsResponse?.accessibility_filters?.allFilterTags?.find(
                        (tag) => tag.tag_name === tagName,
                      );

                    return {
                      tagId: filterTag?.tag_id || index + 1,
                      tagName: tagName,
                      categoryDisplaySectionId: "accessibility-features",
                    };
                  }) || [],
              tags: [
                {
                  tagName: photo.level_name || "Unknown Space",
                  categoryDisplaySectionId: photo.category_id?.toString() || "",
                },
              ],
              displaySections: (() => {
                const sections: Record<string, string> = {};

                if (photo.accessibility_tag_details?.length) {
                  // Use detailed section information - map section ID to section name
                  photo.accessibility_tag_details.forEach((tagDetail) => {
                    sections[tagDetail.sectionId] = tagDetail.sectionName;
                  });
                } else {
                  // Fallback to default section (ID -> Name mapping)
                  sections["accessibility-features"] = i18n.get("accessibilityFeatures");
                }

                if (Object.keys(sections).length === 0) {
                  sections["accessibility-features"] = i18n.get("accessibilityFeatures");
                }

                return sections;
              })(),
              venueId: venueDetailsResponse?.details?.id,
              venueName: venueDetailsResponse?.details?.name,
              venueAddress: venueDetailsResponse?.details?.address,
              user_roles: {
                isGeneralUser: false,
              },
            }))}
            currentIndex={currentSubcategoryPhotos.findIndex(
              (p) => p === currentImage,
            )}
            onClose={() => setFullImageView(false)}
            onFlag={handleFlagPhoto}
            showFlag={true}
            showRightTab={true}
          />
        )}

        {/* Flag Photo Modal */}
        <FlagVenuePhoto
          photoReviewId={selectedImage?.review_photos_id}
          venueName={venueDetailsResponse?.details?.name}
          isOpen={isFlagVenuePhotoOpen}
          onClose={() => handleFlagVenuePhotoClose()}
          venueDetails={{
            data: {
              id: venueDetailsResponse?.details?.id,
              brandId: venueDetailsResponse?.details?.brandId,
              brandName: venueDetailsResponse?.details?.brandName,
              address: venueDetailsResponse?.details?.address,
              name: venueDetailsResponse?.details?.name,
              types: venueDetailsResponse?.details?.types,
              business_photos: venueDetailsResponse?.details?.businessPhotos,
            },
          }}
        />
      </div>
    </>
  );
}

export default AccessibilityPhotosTab;
